import classNames from 'classnames';
import { Fragment, useEffect } from 'react';
import { getStore } from 'app/hermes-redux';
import Language from 'language';
import LoginForm from './login-form/login-form';
import InvitationCodeForm from './invitation-code-form/invitation-code-form';
import ResendInvitationForm from './resend-invitation-form/resend-invitation-form';
import ResendInvitationSuccess from './resend-invitation-success/resend-invitation-success';
import Spinner from 'wa-storybook/components/general/spinner/spinner';
import {
  getAppStoreLogo,
  getGooglePlayLogo,
} from 'app/constants/app-store-logos';
import ModalitiesModal from 'modal-templates/templates/modalities-modal/modalities-modal';
import { openModal } from 'state/modal/actions';
import queryString from 'query-string';
import SessionInvalidationAlertModal from 'wa-storybook/components/modal/session-invalidation-alert-modal/session-invalidation-alert-modal';
import { sessionInvalidationTypes } from 'wa-storybook/components/modal/session-invalidation-alert-modal/constants';
import { setIsLoggingOut } from 'state/session/actions';

import styles from './entry.module.scss';
import { resetConsentState } from '../../state/consent/actions';
import { clearSignupState } from '../signup/main/state/actions';
import { isUnifiedLoginEnabled } from 'globals/testable';
import { LoginFormUSL } from './usl-login-form/usl-login-form';

type EntryType =
  | 'signup'
  | 'login'
  | 'resend-invitation'
  | 'resend-invitation-success';

type Props = {
  isSSOLoading?: boolean;
  type: EntryType;
};

const Entry = ({ isSSOLoading, type }: Props) => {
  const { dispatch } = getStore();
  const companyName = getStore().getState().company.nickname;

  const isType = (entryType: EntryType) => type === entryType;
  const isUnifiedLogin = isUnifiedLoginEnabled();

  useEffect(() => {
    const { timeoutAmount, timeoutUnit, isGenericInvalidation } =
      queryString.parse(location.search);

    const isSessionTimeout = !!(timeoutAmount && timeoutUnit);

    const isSessionInvalidating = isSessionTimeout || !!isGenericInvalidation;

    dispatch(setIsLoggingOut(false));
    dispatch(resetConsentState());
    clearSignupState();

    let payload: any = {
      fitContents: true,
      closeButtonClassName:
        'container__close-button--session-invalidation-alert-modal',
    };

    if (isSessionTimeout) {
      payload = {
        ...payload,
        type: sessionInvalidationTypes.sessionTimeout,
        data: {
          amount: timeoutAmount,
          unit: timeoutUnit,
        },
      };
    } else if (isGenericInvalidation) {
      payload = {
        ...payload,
        type: sessionInvalidationTypes.authMethodOrMFAChange,
      };
    }

    if (isSessionInvalidating) {
      dispatch(openModal(SessionInvalidationAlertModal, payload));
    }
  }, []);

  const phoneNumbers =
    getStore().getState().company.eap_phone_number_login_page;
  const hasPhoneNumbers: boolean = phoneNumbers && phoneNumbers.length > 0;

  const openHelpModal = () => {
    dispatch(
      openModal(ModalitiesModal, {
        isCrossButton: false,
        showCareNowLink: false,
        isLoginPage: true,
      }),
    );
  };

  return (
    <Fragment>
      {isSSOLoading ? (
        <Spinner top='50vh' />
      ) : (
        <div className={styles.container}>
          <div className={classNames(styles['container__image'])} />
          <div className={styles['container__form']}>
            {type === 'signup' ? (
              <InvitationCodeForm />
            ) : isType('login') ? (
              isUnifiedLogin ? (
                <LoginFormUSL />
              ) : (
                <LoginForm
                  openHelpModal={openHelpModal}
                  hasPhoneNumbers={hasPhoneNumbers}
                />
              )
            ) : isType('resend-invitation') ? (
              <ResendInvitationForm />
            ) : (
              isType('resend-invitation-success') && <ResendInvitationSuccess />
            )}
          </div>
          <div className={styles['apps-footer']}>
            <a
              target='_blank'
              rel='noopener noreferrer'
              href={polyglot.t('login.mobile_app_banner.external_link_ios')}
              className={styles['apps-footer__app-link']}
            >
              <img
                src={getAppStoreLogo(Language.getLowerCaseTranslationLocale())}
                alt={polyglot.t('login.native_app_links.app_store_telus')}
              />
            </a>
            <a
              target='_blank'
              rel='noopener noreferrer'
              href={polyglot.t('login.mobile_app_banner.external_link_android')}
              className={styles['apps-footer__app-link']}
            >
              <img
                src={getGooglePlayLogo(
                  Language.getLowerCaseTranslationLocale(),
                )}
                alt={polyglot.t('login.native_app_links.google_play_telus')}
              />
            </a>
            <div className={styles['apps-footer__text']}>
              <div className={styles['apps-footer__text--main']}>
                {polyglot.t('login.mobile_app_banner.main_text_telus')}
              </div>
              <div className={styles['apps-footer__text--secondary']}>
                {polyglot.t('login.mobile_app_banner.secondary_text', {
                  company: companyName || '',
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

Entry.displayName = 'Entry';

export default Entry;
